import { Component, type ReactNode, type ErrorInfo } from 'react';
import * as Sentry from '@sentry/react';
import i18n from 'i18next';
import { Button } from './Button';
import { mayReloadForChunk, RELOAD_FLAG } from '@/shared/lib/chunk-reload';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * A lazily-loaded route chunk went missing — almost always because a new build
 * was deployed and this tab is still holding the previous index.html, so the
 * old (content-hashed) chunk filename no longer exists on the server. The only
 * recovery is a fresh page load, which pulls the new index.html + chunks.
 */
function isChunkLoadError(error: Error | null): boolean {
  const msg = error?.message ?? '';
  return (
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    error?.name === 'ChunkLoadError'
  );
}


export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Stale chunk after a deploy — reload once to fetch the new build instead of
    // showing an error the user can't recover from by retrying. The session flag
    // prevents an infinite reload loop if the failure is genuine.
    if (isChunkLoadError(error) && mayReloadForChunk()) {
      window.location.reload();
      return;
    }
    Sentry.captureException(error, {
      contexts: { react: { componentStack: info.componentStack ?? '' } },
    });
  }

  handleReset = () => {
    // For a chunk error, resetting state just re-attempts the same dead import —
    // hard reload instead so the new build is fetched.
    if (isChunkLoadError(this.state.error)) {
      sessionStorage.removeItem(RELOAD_FLAG);
      window.location.reload();
      return;
    }
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div className="min-h-[40vh] flex items-center justify-center p-6">
          <div className="text-center max-w-md">
            <div className="text-[40px] mb-3">😵</div>
            {/* i18n.t, not the hook: this is a class component and may render
                when React context is what broke. */}
            <h2 className="font-display text-title text-[var(--color-ink)] mb-2">
              {i18n.t('common.error', { defaultValue: 'Something went wrong' })}
            </h2>
            <p className="text-ui text-[var(--color-ink-2)] mb-4 leading-[1.6]">
              {i18n.t('auth.errors.generic', { defaultValue: 'Something went wrong. Please try again.' })}
            </p>
            {/* The raw message is for developers only; users never see it. */}
            {import.meta.env.DEV && this.state.error && (
              <pre className="text-caption text-[var(--color-error)] bg-[var(--color-error-soft)] p-3 rounded-lg mb-4 text-start overflow-x-auto max-h-[120px]">
                {this.state.error.message}
              </pre>
            )}
            <Button variant="primary" onClick={this.handleReset}>
              {i18n.t('common.retry', { defaultValue: 'Try again' })}
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
